/**
 * The sound controls (ADR-0064, moved into the settings panel by ADR-0105): the
 * master **volume slider** and **mute button** living in the Sound section of the
 * settings panel (`#settings-panel` in `index.html`, shell wired by `settings.ts`).
 * They once lived in the top-right toolbar as a mute toggle with a hover-revealed
 * vertical fader; the panel gives them a stable, discoverable home instead, so the
 * hover-reveal machinery is gone.
 *
 * Like `chat.ts`, `settings.ts`, and `wardrobe.ts`, this module owns **only** the DOM
 * wiring; the markup and styling live in `index.html`, and it touches no Pixi and no
 * socket. It is therefore unit-testable headlessly (jsdom).
 *
 * The mute button (`#sound-button`) flips the master mute and reflects the state (a
 * 🔇/🔉/🔊 glyph carrying both mute *and* a sense of level, plus aria). The slider
 * (`#volume-slider`) rides the master volume live as it drags (or via its arrow
 * keys). Both choices persist in localStorage so they survive a reload - exactly how
 * `main.ts` persists the speech-text hue. Mute and level are independent: muting
 * silences without disturbing the level, so unmuting restores the prior loudness.
 * The persisted choices are pushed to the audio layer at startup (via
 * `onMuteChange`/`onVolumeChange`), so a player's mix is restored before they touch
 * anything.
 */

/** Local-storage key the player's sound on/off (mute) choice persists under. */
const MUTE_KEY = 'britbank.muted';
/** Local-storage key the player's master volume level (`0..100`) persists under. */
const VOLUME_KEY = 'britbank.volume';

/**
 * Read the saved mute choice, defaulting to **unmuted** (sound on) when nothing is
 * stored or storage is unavailable. Mirrors `main.ts`'s `loadSpeechHue`: a private-mode
 * browser simply gets the default and a session-only choice.
 */
function loadMuted(doc: Document): boolean {
  try {
    return windowOf(doc).localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist the mute choice so it survives a reload/reconnect. */
function saveMuted(doc: Document, muted: boolean): void {
  try {
    windowOf(doc).localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/**
 * Read the saved volume level as a fraction in `[0, 1]`, defaulting to **full** (1) when
 * nothing is stored, the value is unparseable, or storage is unavailable. Stored as an
 * integer `0..100` to match the range input's units.
 */
function loadVolume(doc: Document): number {
  try {
    const raw = windowOf(doc).localStorage.getItem(VOLUME_KEY);
    if (raw === null) return 1;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return 1;
    return clamp01(pct / 100);
  } catch {
    return 1;
  }
}

/** Persist the volume level (as an integer percent) so it survives a reload/reconnect. */
function saveVolume(doc: Document, level: number): void {
  try {
    windowOf(doc).localStorage.setItem(VOLUME_KEY, String(Math.round(clamp01(level) * 100)));
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/** Clamp a number into `[0, 1]`. */
function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/** The window owning a document (the live `window` in the app, the jsdom one in tests). */
function windowOf(doc: Document): Window & typeof globalThis {
  return (doc.defaultView ?? window) as Window & typeof globalThis;
}

/** Options for {@link initSound}; `doc` is injected by tests. */
export interface SoundOptions {
  /**
   * Apply the master mute to the audio layer. Called once at startup with the
   * persisted choice (so a saved mute takes effect immediately) and again on every
   * toggle. `main.ts` wires this to `AudioManager.setMuted`.
   */
  onMuteChange: (muted: boolean) => void;
  /**
   * Apply the master volume level (`[0, 1]`) to the audio layer. Called once at startup
   * with the persisted choice and again live as the slider drags. `main.ts` wires this
   * to `AudioManager.setVolume`. Optional so existing callers/tests need no change.
   */
  onVolumeChange?: (level: number) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts` drives the sound controls through. */
export interface SoundHandle {
  /** Whether sound is currently muted. */
  isMuted(): boolean;
  /** Set the mute state programmatically (reflects, persists, and notifies). */
  setMuted(muted: boolean): void;
  /** The current master volume level in `[0, 1]`. */
  getVolume(): number;
}

export function initSound(opts: SoundOptions): SoundHandle {
  const doc = opts.doc ?? document;
  const soundBtn = doc.getElementById('sound-button');
  const slider = doc.getElementById('volume-slider') as HTMLInputElement | null;

  let muted = loadMuted(doc);
  let level = loadVolume(doc);

  /** Paint the mute button + slider to match the current mute + level state. */
  const reflect = (): void => {
    if (soundBtn) {
      soundBtn.setAttribute('aria-pressed', String(muted));
      soundBtn.setAttribute('aria-label', muted ? 'Unmute sound' : 'Mute sound');
      soundBtn.title = muted ? 'Sound off — click to unmute' : 'Sound on — click to mute';
      // The glyph carries the at-a-glance state for sighted players (the aria-label
      // carries the mute action for AT): a crossed speaker when muted or silent, then a
      // one- or two-wave speaker as a coarse sense of how loud the slider is set.
      soundBtn.textContent = muted || level <= 0 ? '🔇' : level < 0.5 ? '🔉' : '🔊';
    }
    // Keep the slider's value + aria in sync with the level (e.g. after a programmatic
    // setMuted, or restoring the persisted level at startup).
    if (slider) {
      const pct = Math.round(level * 100);
      slider.value = String(pct);
      slider.setAttribute('aria-valuenow', String(pct));
      slider.setAttribute('aria-valuetext', `${pct}%`);
    }
  };

  /** Move mute to `next`, optionally persisting, then reflect + notify the audio layer. */
  const applyMute = (next: boolean, persist: boolean): void => {
    muted = next;
    if (persist) saveMuted(doc, muted);
    reflect();
    opts.onMuteChange(muted);
  };

  /** Move the volume to `next` (`[0, 1]`), optionally persisting, then reflect + notify. */
  const applyVolume = (next: number, persist: boolean): void => {
    level = clamp01(next);
    if (persist) saveVolume(doc, level);
    reflect();
    opts.onVolumeChange?.(level);
  };

  soundBtn?.addEventListener('click', () => applyMute(!muted, true));

  slider?.addEventListener('input', () => {
    const pct = Number(slider.value);
    applyVolume(Number.isFinite(pct) ? pct / 100 : level, true);
  });

  // Paint the saved state and push it to the audio layer up front, so a player who
  // muted / turned the volume down last session has their mix restored before they
  // touch anything.
  reflect();
  opts.onMuteChange(muted);
  opts.onVolumeChange?.(level);

  return {
    isMuted: () => muted,
    setMuted: (next) => applyMute(next, true),
    getVolume: () => level,
  };
}
